// Generic Task Nomenclature List
// Reusable component for displaying and managing task nomenclatures

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Plus } from 'lucide-react';
import { translateNomenclature } from '../../../utils/translateNomenclature';
import { useTaskNomenclature, useTaskNomenclatureMutations } from '../../../hooks/useTaskNomenclatures';
import './GenericTaskNomenclatureList.css';

const GenericTaskNomenclatureList = ({ nomenclatureType, userId }) => {
  const { t } = useTranslation();
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const { items, loading, error, refresh } = useTaskNomenclature(nomenclatureType);
  const { create, update, delete: deleteItem } = useTaskNomenclatureMutations(nomenclatureType);

  // Reset form when nomenclature type changes
  React.useEffect(() => {
    setEditingItem(null);
    setFormData({});
  }, [nomenclatureType]);

  const handleAdd = () => {
    setEditingItem('new');
    setFormData({ name: '', description: '', isActive: true });
  };

  const handleEdit = (item) => {
    setEditingItem(item.id);
    setFormData(item);
  };

  const handleSave = async () => {
    try {
      if (!userId) {
        alert('Error: User ID is not available. Please refresh the page and try again.');
        return;
      }
      
      if (!formData.name || formData.name.trim() === '') {
        alert('Error: Name is required.');
        return;
      }
      
      if (editingItem === 'new') {
        await create(formData, userId);
      } else {
        await update(editingItem, formData, userId);
      }
      setEditingItem(null);
      setFormData({});
      refresh();
    } catch (error) {
      alert(`Error saving: ${error.message}`);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await deleteItem(id, userId);
      refresh();
    } catch (error) {
      alert(`Error deleting: ${error.message}`);
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setFormData({});
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error: {error}</p>
        <button onClick={refresh} className="btn btn-secondary">Retry</button>
      </div>
    );
  }

  return (
    <div className="generic-nomenclature-list">
      <div className="list-header">
        <h3>{nomenclatureType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={16} /> Add New
        </button>
      </div>

      {editingItem && (
        <div className="edit-form">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter name"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter description"
              rows={2}
            />
          </div>

          <div className="form-actions">
            <button className="btn btn-cancel" onClick={handleCancel}>Cancel</button>
            <button className="btn btn-save" onClick={handleSave}>Save</button>
          </div>
        </div>
      )}

      <div className="items-list">
        {items.map(item => (
          <div key={item.id} className="nomenclature-item">
            <div className="item-content">
              <div className="item-name">{translateNomenclature(t, nomenclatureType, item)}</div>
              {item.description && (
                <div className="item-description">{item.description}</div>
              )}
            </div>
            <div className="item-actions">
              <button
                className="btn btn-secondary"
                onClick={() => handleEdit(item)}
                title="Edit"
              >
                <Edit size={14} />
              </button>
              <button
                className="btn btn-delete btn-icon"
                onClick={() => handleDelete(item.id, item.name)}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="empty-hint">
          No items found. Click "Add New" to create one.
        </div>
      )}
    </div>
  );
};

export default GenericTaskNomenclatureList;

